import React from 'react';
import moment from 'moment-timezone';

import { Box, Chip, Stack, Typography } from '../../../ui/core';
import { scrollY } from '../../../ui/styles';

/** "62 mph", or null when the fix carries no speed (a passive fix, an older app). */
/**
 * "~12 min late" / "~3 min early" / "on time" for a forecast from the
 * location service (first pick-up or return); null without one.
 */
export const latenessText = (minutes) => {
  if (!Number.isFinite(minutes)) return null;
  if (Math.abs(minutes) <= 1) return 'on time';
  return minutes > 0 ? `~${Math.round(minutes)} min late` : `~${Math.round(-minutes)} min early`;
};

export const speedText = (driver) => (Number.isFinite(driver?.speedMph) ? `${Math.round(driver.speedMph)} mph` : null);

const Field = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography color="text.secondary" variant="body2">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>
      {value}
    </Typography>
  </Stack>
);

const routeText = (route) => {
  if (!route) return 'absent';
  const miles = (route.distanceMeters / 1609.34).toFixed(1);
  const minutes = Math.round(route.durationSeconds / 60);
  const legs = route.legs?.length ?? 0;
  return `${miles} mi · ${Math.floor(minutes / 60)}h ${minutes % 60}m · ${legs} leg${legs === 1 ? '' : 's'}`;
};

const etasText = (etas) => {
  if (!etas?.length) return 'absent';
  const next = etas[0]?.eta ? moment(etas[0].eta).format('h:mm:ss A') : '—';
  return `next ${next} · ${etas.length} stop${etas.length === 1 ? '' : 's'}`;
};

const driverText = (driver) =>
  driver ? `${driver.lat.toFixed(5)}, ${driver.lon.toFixed(5)} @ ${Math.round(driver.bearing ?? 0)}°` : 'absent';

const fixText = (driver) => (driver ? `${moment(driver.timestamp).format('h:mm:ss A')} · ${driver.source}` : '—');

const ridersText = (riders) => {
  if (!riders?.length) return 'none';
  const newest = riders.reduce((a, b) => (Date.parse(a.timestamp) > Date.parse(b.timestamp) ? a : b));
  return `${riders.length} awaiting pick-up · newest ${moment(newest.timestamp).format('h:mm:ss A')}`;
};

const ridesText = (rides) => (rides?.length ? rides.map((r) => r.status).join(' · ') : 'none');

/**
 * The trip as the live stream has it right now — `/admin/live/trip/:tripId`,
 * unrendered: status and progress through the stops, the route the driver is
 * following, the ETAs, and the car's last fix. The trip-level counterpart of
 * the ride page's Live state, and the place to check a trip-stream change
 * before it ships.
 */
const TripLiveState = ({ live }) => {
  const { trip, route, etas, driver, riders, feed = [], connected } = live;
  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">Live state</Typography>
        <Chip
          size="small"
          variant="outlined"
          color={connected ? 'success' : 'default'}
          label={connected ? 'Streaming' : 'Connecting'}
        />
      </Stack>

      {trip ? (
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={trip.status} />
            <Typography color="text.secondary" variant="body2">
              stop {Math.min((trip.currentWaypoint ?? 0) + 1, trip.waypoints?.length ?? 0)} of{' '}
              {trip.waypoints?.length ?? 0}
            </Typography>
          </Stack>
          <Field label="rides" value={ridesText(trip.rides)} />
          <Field label="route" value={routeText(route)} />
          <Field label="etas" value={etasText(etas)} />
          <Field label="driverLocation" value={driverText(driver)} />
          <Field label="lastFix" value={fixText(driver)} />
          <Field label="speed" value={speedText(driver) || '—'} />
          <Field label="pickupLate" value={latenessText(driver?.pickupLateMinutes) || '—'} />
          <Field label="returnLate" value={latenessText(driver?.returnLateMinutes) || '—'} />
          <Field label="riderLocations" value={ridersText(riders)} />

          {/* The stream as it arrives, newest first. Bounded in the hook, and
              in height here so it can't push the stops off the panel. */}
          {feed.length > 0 && (
            <Box sx={{ ...scrollY, maxHeight: 160, mt: 0.5 }}>
              {feed.map((entry, i) => (
                <Stack
                  key={`${entry.at}-${i}`}
                  direction="row"
                  spacing={1}
                  sx={{ fontFamily: 'monospace', fontSize: 11, py: '2px' }}
                >
                  <Typography component="span" variant="inherit" color="text.secondary">
                    {moment(entry.at).format('h:mm:ss A')}
                  </Typography>
                  <Typography component="span" variant="inherit" sx={{ minWidth: 0 }}>
                    {entry.line}
                  </Typography>
                </Stack>
              ))}
            </Box>
          )}
        </Stack>
      ) : (
        <Typography color="text.secondary" variant="body2">
          {connected ? 'Waiting for the trip…' : 'Not connected to the trip stream.'}
        </Typography>
      )}
    </Stack>
  );
};

export default TripLiveState;
